/// @deprecated Token storage moved to HttpOnly cookies on the server.
/// This module now only holds OAuth state (CSRF-bound nonce) in
/// sessionStorage so the OAuth callback can verify that the state value it
/// receives matches one we generated.
///
/// Browser clients never touch access or refresh JWTs — those live in
/// `meridian_session` / `meridian_refresh` cookies with `HttpOnly; Secure;
/// SameSite=Lax` flags set by the server. XSS on this origin can't read
/// them.

const OAUTH_STATE_KEY = 'meridian_oauth_state';
const OAUTH_PKCE_KEY = 'meridian_oauth_pkce_verifier';
const OAUTH_REAUTH_PROOF_KEY = 'meridian_oauth_reauth_proof';
const OAUTH_REAUTH_INTENT_KEY = 'meridian_oauth_reauth_intent';

export type OAuthReauthIntent = 'security' | 'account_deletion';

export function rememberOAuthReauthProof(proof: string): void {
  sessionStorage.setItem(OAUTH_REAUTH_PROOF_KEY, proof);
}

export function hasOAuthReauthProof(): boolean {
  return sessionStorage.getItem(OAUTH_REAUTH_PROOF_KEY) !== null;
}

export function consumeOAuthReauthProof(): string | null {
  const proof = sessionStorage.getItem(OAUTH_REAUTH_PROOF_KEY);
  sessionStorage.removeItem(OAUTH_REAUTH_PROOF_KEY);
  return proof;
}

/// Remember which sensitive account-center flow initiated provider
/// reauthentication. The OAuth callback consumes this value exactly once and
/// maps the closed enum to an in-app route; callers cannot turn it into an open
/// redirect.
export function rememberOAuthReauthIntent(intent: OAuthReauthIntent): void {
  sessionStorage.setItem(OAUTH_REAUTH_INTENT_KEY, intent);
}

export function consumeOAuthReauthIntent(): OAuthReauthIntent {
  const intent = sessionStorage.getItem(OAUTH_REAUTH_INTENT_KEY);
  sessionStorage.removeItem(OAUTH_REAUTH_INTENT_KEY);
  return intent === 'account_deletion' ? intent : 'security';
}

export async function createOAuthPkce(purpose: 'auth' | 'link' | 'reauth' = 'link'): Promise<{ challenge: string }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const verifier = base64url(bytes);
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  sessionStorage.setItem(`${OAUTH_PKCE_KEY}:${purpose}`, verifier);
  return { challenge: base64url(new Uint8Array(digest)) };
}

export function consumeOAuthPkceVerifier(purpose: 'auth' | 'link' | 'reauth' = 'link'): string | null {
  const key = `${OAUTH_PKCE_KEY}:${purpose}`;
  const verifier = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  return verifier;
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

/// Remember the OAuth state parameter we handed to the provider. Stored
/// in sessionStorage so it survives the redirect away and back, but not
/// across tabs.
export function rememberOAuthState(state: string): void {
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
  } catch {
    // sessionStorage can throw in private-browsing modes; fall through,
    // the callback just won't get CSRF protection this round.
  }
}

/// Return the remembered OAuth state and clear it (single-use).
export function consumeOAuthState(): string | null {
  try {
    const value = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    return value;
  } catch {
    return null;
  }
}

/// Pull the `state` query parameter out of an OAuth-redirect URL the
/// server handed us and remember it, so the callback can compare. We
/// stash the entire signed state — that's enough to reject a mismatching
/// callback. Tolerates malformed URLs (server sent something unexpected)
/// by letting the redirect happen anyway; the callback fails safely with
/// `no_session_state`.
export function rememberStateFromRedirect(url: string): void {
  try {
    const parsed = new URL(url);
    const state = parsed.searchParams.get('state');
    if (state) rememberOAuthState(state);
  } catch {
    // Malformed URL — let the redirect through and let the callback fail.
  }
}
